import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';

const EmergencyAlertsSimple = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const [settings, setSettings] = useState({
    email: {
      enabled: true,
      smtp_server: 'smtp-mail.outlook.com',
      smtp_port: 587,
      username: 'test@sherborneqatar.org',
      password: ''
    },
    sms: {
      enabled: false,
      service: 'twilio',
      account_sid: '',
      auth_token: '',
      from_number: ''
    },
    teams: {
      enabled: false,
      webhook_url: '',
      channel_name: 'Emergency Alerts'
    },
    slack: {
      enabled: false,
      webhook_url: '',
      channel: '#emergency'
    },
    whatsapp: {
      enabled: false,
      api_key: '',
      instance_id: ''
    },
    push_notifications: {
      enabled: false,
      firebase_key: '',
      app_id: ''
    },
    pa_system: {
      enabled: false,
      api_endpoint: '',
      api_key: ''
    },
    desktop_alerts: {
      enabled: true
    },
    database_alerts: {
      enabled: true
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [testResults, setTestResults] = useState(null);
  const [staffList, setStaffList] = useState([]);

  useEffect(() => {
    loadSettings();
    loadStaffList();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(buildApiUrl('/emergency-notification-settings'));
      if (response.ok) {
        const data = await response.json();
        // Merge with existing settings to ensure all properties exist
        setSettings(prev => ({
          email: { 
            ...prev.email, 
            ...(data.email || {}),
            // Handle legacy from_email field - use it as username if username is empty
            username: data.email?.username || data.email?.from_email || prev.email.username
          },
          sms: { ...prev.sms, ...(data.sms || {}) },
          teams: { ...prev.teams, ...(data.teams || {}) },
          slack: { ...prev.slack, ...(data.slack || {}) },
          whatsapp: { ...prev.whatsapp, ...(data.whatsapp || {}) },
          push_notifications: { ...prev.push_notifications, ...(data.push_notifications || {}) },
          pa_system: { ...prev.pa_system, ...(data.pa_system || {}) },
          desktop_alerts: { ...prev.desktop_alerts, ...(data.desktop_alerts || {}) },
          database_alerts: { ...prev.database_alerts, ...(data.database_alerts || {}) }
        }));
      } else {
        console.warn('Notification settings API not available, trying localStorage');
        // Try to load from localStorage as fallback
        const savedSettings = localStorage.getItem('emergency_notification_settings');
        if (savedSettings) {
          const data = JSON.parse(savedSettings);
          // Merge with existing settings to ensure all properties exist
          setSettings(prev => ({
            email: { ...prev.email, ...(data.email || {}) },
            sms: { ...prev.sms, ...(data.sms || {}) },
            teams: { ...prev.teams, ...(data.teams || {}) },
            slack: { ...prev.slack, ...(data.slack || {}) },
            whatsapp: { ...prev.whatsapp, ...(data.whatsapp || {}) },
            push_notifications: { ...prev.push_notifications, ...(data.push_notifications || {}) },
            pa_system: { ...prev.pa_system, ...(data.pa_system || {}) },
            desktop_alerts: { ...prev.desktop_alerts, ...(data.desktop_alerts || {}) },
            database_alerts: { ...prev.database_alerts, ...(data.database_alerts || {}) }
          }));
        }
      }
    } catch (error) {
      console.warn('Error loading settings, trying localStorage:', error);
      // Try to load from localStorage as fallback
      const savedSettings = localStorage.getItem('emergency_notification_settings');
      if (savedSettings) {
        try {
          const data = JSON.parse(savedSettings);
          // Merge with existing settings to ensure all properties exist
          setSettings(prev => ({
            email: { 
              ...prev.email, 
              ...(data.email || {}),
              // Handle legacy from_email field - use it as username if username is empty
              username: data.email?.username || data.email?.from_email || prev.email.username
            },
            sms: { ...prev.sms, ...(data.sms || {}) },
            teams: { ...prev.teams, ...(data.teams || {}) },
            slack: { ...prev.slack, ...(data.slack || {}) },
            whatsapp: { ...prev.whatsapp, ...(data.whatsapp || {}) },
            push_notifications: { ...prev.push_notifications, ...(data.push_notifications || {}) },
            pa_system: { ...prev.pa_system, ...(data.pa_system || {}) },
            desktop_alerts: { ...prev.desktop_alerts, ...(data.desktop_alerts || {}) },
            database_alerts: { ...prev.database_alerts, ...(data.database_alerts || {}) }
          }));
        } catch (parseError) {
          console.error('Error parsing saved settings:', parseError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStaffList = async () => {
    console.log('Starting loadStaffList function');
    try {
      // Skip API for now and go directly to local file for debugging
      console.log('Trying to fetch /staffemails.json from public folder');
      const response = await fetch('/staffemails.json');
      console.log('Fetch response:', response.status, response.statusText);
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        console.log('Content-Type:', contentType);
        
        if (contentType && contentType.includes('application/json')) {
          const text = await response.text();
          console.log('Raw response text:', text);
          
          try {
            const data = JSON.parse(text);
            console.log('Loaded staff data from JSON:', data, 'Type:', typeof data, 'IsArray:', Array.isArray(data));
            // Handle different JSON structures and ensure it's always an array
            let staffData = [];
            if (Array.isArray(data)) {
              staffData = data;
            } else if (data.staff && Array.isArray(data.staff)) {
              staffData = data.staff;
            } else if (data && typeof data === 'object') {
              // If it's an object but not an array, convert to array
              staffData = [data];
            }
            console.log('Setting staffList to:', staffData);
            setStaffList(staffData);
            return; // Exit early on success
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            setStaffList([]);
          }
        } else {
          console.error('staffemails.json is not returning JSON content, content-type:', contentType);
          const text = await response.text();
          console.log('Non-JSON response:', text);
          setStaffList([]);
        }
      } else {
        console.warn('staffemails.json not found in public folder, status:', response.status);
        setStaffList([]);
      }
    } catch (error) {
      console.error('Error loading staff list:', error);
      setStaffList([]);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      // Prepare settings for backend - ensure from_email matches username for compatibility
      const settingsToSave = {
        ...settings,
        email: {
          ...settings.email,
          from_email: settings.email.username // Backend still expects from_email
        }
      };
      
      const response = await fetch(buildApiUrl('/emergency-notification-settings'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settingsToSave)
      });
      
      if (response.ok) {
        alert('Settings saved successfully!');
        // Reload settings from backend to ensure UI reflects actual saved state
        loadSettings();
      } else if (response.status === 405) {
        alert('Settings API not available yet. Settings will be stored locally for now.');
        // Store settings in localStorage as fallback
        localStorage.setItem('emergency_notification_settings', JSON.stringify(settings));
      } else {
        alert('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Settings API not available. Storing locally for now.');
      localStorage.setItem('emergency_notification_settings', JSON.stringify(settings));
    } finally {
      setSaving(false);
    }
  };

  const testNotifications = async () => {
    if (!testRecipient) {
      alert('Please enter a test recipient email');
      return;
    }

    try {
      setTestResults(null);
      const response = await fetch(buildApiUrl('/emergency-notification-settings/test'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          test_recipient: testRecipient
        })
      });

      if (response.ok) {
        const data = await response.json();
        setTestResults(data.results || []);
      } else if (response.status === 405) {
        // API not available, show mock results
        const mockResults = [
          { method: 'email', status: 'api_not_available', sent: 0 },
          { method: 'sms', status: 'disabled', sent: 0 },
          { method: 'teams', status: 'disabled', sent: 0 },
          { method: 'slack', status: 'disabled', sent: 0 },
          { method: 'whatsapp', status: 'disabled', sent: 0 },
          { method: 'push_notifications', status: 'disabled', sent: 0 },
          { method: 'pa_system', status: 'disabled', sent: 0 },
          { method: 'desktop_alerts', status: 'api_not_available', sent: 0 },
          { method: 'database_alerts', status: 'api_not_available', sent: 0 }
        ];
        setTestResults(mockResults);
        alert('Test API not available yet. Showing mock results.');
      } else {
        alert('Failed to test notifications');
      }
    } catch (error) {
      console.error('Error testing notifications:', error);
      alert('Test API not available yet.');
    }
  };

  const updateSettings = (category, field, value) => {
    setSettings(prev => {
      // Ensure the category exists
      if (!prev[category]) {
        prev[category] = {};
      }
      
      return {
        ...prev,
        [category]: {
          ...prev[category],
          [field]: value
        }
      };
    });
  };

  // Helper function to safely access nested settings properties
  const getSetting = (category, field, defaultValue = '') => {
    if (!settings || !settings[category]) {
      return defaultValue;
    }
    const value = settings[category][field];
    return value !== undefined && value !== null ? value : defaultValue;
  };

  // Helper function to check if a category is enabled
  const isEnabled = (category) => {
    const enabled = settings && settings[category] && settings[category].enabled === true;
    console.log(`isEnabled(${category}):`, enabled, 'settings[category]:', settings[category]);
    return enabled;
  };

  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading emergency alerts settings...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">
          🚨 Emergency Notification Settings
        </h2>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <span>💾</span>
              <span>Save Settings</span>
            </>
          )}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 border-b">
        {[
          { id: 'settings', name: 'Notification Methods', icon: '⚙️' },
          { id: 'test', name: 'Test Alerts', icon: '🧪' },
          { id: 'staff', name: 'Staff Contacts', icon: '👥' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-t-lg transition-colors flex items-center space-x-2 ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Email Settings */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                <span>📧</span>
                <span>Email Notifications</span>
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEnabled('email')}
                  onChange={(e) => updateSettings('email', 'enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            {isEnabled('email') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Server</label>
                  <input
                    type="text"
                    value={getSetting('email', 'smtp_server', 'smtp-mail.outlook.com')}
                    onChange={(e) => updateSettings('email', 'smtp_server', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="smtp-mail.outlook.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SMTP Port</label>
                  <input
                    type="number"
                    value={getSetting('email', 'smtp_port', 587)}
                    onChange={(e) => updateSettings('email', 'smtp_port', parseInt(e.target.value) || 587)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input
                    type="email"
                    value={getSetting('email', 'username', 'test@sherborneqatar.org')}
                    onChange={(e) => updateSettings('email', 'username', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="test@sherborneqatar.org"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={getSetting('email', 'password')}
                    onChange={(e) => updateSettings('email', 'password', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Your Microsoft account password"
                  />
                </div>
                <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-blue-600 mt-0.5">ℹ️</span>
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">Microsoft Email Configuration:</p>
                      <p>• Emails will be sent from your login email address</p>
                      <p>• Use your Microsoft account password</p>
                      <p>• Default SMTP: smtp-mail.outlook.com:587</p>
                    </div>
                  </div>
                </div>
                <div className="col-span-2 bg-orange-50 border border-orange-200 rounded-md p-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-orange-600 mt-0.5">⚠️</span>
                    <div className="text-sm text-orange-800">
                      <p className="font-medium">Netlify Deployment Note:</p>
                      <p>• Email works locally but may need environment variables on Netlify</p>
                      <p>• Consider using email service like SendGrid for production</p>
                      <p>• SMTP credentials should be stored in Netlify environment variables</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* SMS Settings */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                <span>📱</span>
                <span>SMS Notifications</span>
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEnabled('sms')}
                  onChange={(e) => updateSettings('sms', 'enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            {isEnabled('sms') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                  <select
                    value={getSetting('sms', 'service', 'twilio')}
                    onChange={(e) => updateSettings('sms', 'service', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="twilio">Twilio</option>
                    <option value="nexmo">Nexmo</option>
                    <option value="aws_sns">AWS SNS</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Number</label>
                  <input
                    type="text"
                    value={getSetting('sms', 'from_number')}
                    onChange={(e) => updateSettings('sms', 'from_number', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account SID</label>
                  <input
                    type="text"
                    value={getSetting('sms', 'account_sid')}
                    onChange={(e) => updateSettings('sms', 'account_sid', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auth Token</label>
                  <input
                    type="password"
                    value={getSetting('sms', 'auth_token')}
                    onChange={(e) => updateSettings('sms', 'auth_token', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Teams Settings */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                <span>💼</span>
                <span>Microsoft Teams</span>
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEnabled('teams')}
                  onChange={(e) => updateSettings('teams', 'enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            {isEnabled('teams') && (
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
                  <input
                    type="url"
                    value={getSetting('teams', 'webhook_url')}
                    onChange={(e) => updateSettings('teams', 'webhook_url', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Channel Name</label>
                  <input
                    type="text"
                    value={getSetting('teams', 'channel_name', 'Emergency Alerts')}
                    onChange={(e) => updateSettings('teams', 'channel_name', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Slack Settings */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                <span>💬</span>
                <span>Slack Notifications</span>
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEnabled('slack')}
                  onChange={(e) => updateSettings('slack', 'enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            {isEnabled('slack') && (
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Webhook URL</label>
                  <input
                    type="url"
                    value={getSetting('slack', 'webhook_url')}
                    onChange={(e) => updateSettings('slack', 'webhook_url', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="https://hooks.slack.com/..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                  <input
                    type="text"
                    value={getSetting('slack', 'channel', '#emergency')}
                    onChange={(e) => updateSettings('slack', 'channel', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="#emergency"
                  />
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp Settings */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                <span>📲</span>
                <span>WhatsApp Business</span>
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEnabled('whatsapp')}
                  onChange={(e) => updateSettings('whatsapp', 'enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            {isEnabled('whatsapp') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                  <input
                    type="password"
                    value={getSetting('whatsapp', 'api_key')}
                    onChange={(e) => updateSettings('whatsapp', 'api_key', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instance ID</label>
                  <input
                    type="text"
                    value={getSetting('whatsapp', 'instance_id')}
                    onChange={(e) => updateSettings('whatsapp', 'instance_id', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Push Notifications Settings */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                <span>🔔</span>
                <span>Push Notifications</span>
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEnabled('push_notifications')}
                  onChange={(e) => updateSettings('push_notifications', 'enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            {isEnabled('push_notifications') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Firebase Key</label>
                  <input
                    type="password"
                    value={getSetting('push_notifications', 'firebase_key')}
                    onChange={(e) => updateSettings('push_notifications', 'firebase_key', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">App ID</label>
                  <input
                    type="text"
                    value={getSetting('push_notifications', 'app_id')}
                    onChange={(e) => updateSettings('push_notifications', 'app_id', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            )}
          </div>

          {/* PA System Settings */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-800 flex items-center space-x-2">
                <span>📢</span>
                <span>PA System</span>
              </h3>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isEnabled('pa_system')}
                  onChange={(e) => updateSettings('pa_system', 'enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            {isEnabled('pa_system') && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API Endpoint</label>
                  <input
                    type="url"
                    value={getSetting('pa_system', 'api_endpoint')}
                    onChange={(e) => updateSettings('pa_system', 'api_endpoint', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="https://..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                  <input
                    type="password"
                    value={getSetting('pa_system', 'api_key')}
                    onChange={(e) => updateSettings('pa_system', 'api_key', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Always Enabled Methods */}
          <div className="border rounded-lg p-4 bg-green-50">
            <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center space-x-2">
              <span>✅</span>
              <span>Always Enabled Methods</span>
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
              <div className="flex items-center space-x-2">
                <span>🖥️</span>
                <span>Desktop Alerts</span>
              </div>
              <div className="flex items-center space-x-2">
                <span>💾</span>
                <span>Database Logging</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'test' && (
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="text-lg font-medium text-yellow-800 mb-2 flex items-center space-x-2">
              <span>🧪</span>
              <span>Test Emergency Notifications</span>
            </h3>
            <p className="text-yellow-700 text-sm mb-4">
              Send a test notification to verify your settings are working correctly.
            </p>
            <div className="flex space-x-4 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Recipient Email</label>
                <input
                  type="email"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="test@example.com"
                />
              </div>
              <button
                onClick={testNotifications}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center space-x-2"
              >
                <span>🚀</span>
                <span>Send Test</span>
              </button>
            </div>
          </div>

          {testResults && (
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-4">Test Results:</h4>
              <div className="space-y-3">
                {testResults.map((result, index) => (
                  <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
                    result.status === 'success' ? 'bg-green-50 border border-green-200' :
                    result.status === 'disabled' ? 'bg-gray-50 border border-gray-200' :
                    'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">
                        {result.method === 'email' ? '📧' :
                         result.method === 'sms' ? '📱' :
                         result.method === 'teams' ? '💼' :
                         result.method === 'slack' ? '💬' :
                         result.method === 'whatsapp' ? '📲' :
                         result.method === 'push_notifications' ? '🔔' :
                         result.method === 'pa_system' ? '📢' :
                         result.method === 'desktop_alerts' ? '🖥️' :
                         result.method === 'database_alerts' ? '💾' : '❓'}
                      </span>
                      <div>
                        <span className="font-medium capitalize">{result.method.replace('_', ' ')}</span>
                        <div className="text-sm text-gray-600">
                          Status: {result.status} | Sent: {result.sent || 0}
                        </div>
                      </div>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${
                      result.status === 'success' ? 'bg-green-100 text-green-800' :
                      result.status === 'disabled' ? 'bg-gray-100 text-gray-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {result.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'staff' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-800">Staff Contact List</h3>
            <span className="text-sm text-gray-600">{Array.isArray(staffList) ? staffList.length : 0} contacts</span>
          </div>
          
          {!Array.isArray(staffList) || staffList.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <span className="text-2xl mb-2 block">👥</span>
              <p className="text-gray-600">No staff contacts loaded</p>
              <p className="text-gray-500 text-sm mt-1">Staff contacts are loaded from staffemails.json</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {staffList.map((staff, index) => (
                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-800">{staff.name}</h4>
                      <p className="text-sm text-gray-600">{staff.email}</p>
                      <p className="text-xs text-gray-500">{staff.role} - {staff.department}</p>
                      {staff.phone && (
                        <p className="text-xs text-gray-500">📞 {staff.phone}</p>
                      )}
                      {staff.notes && (
                        <p className="text-xs text-blue-600 mt-1">💡 {staff.notes}</p>
                      )}
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <div className={`px-2 py-1 rounded text-xs font-medium ${
                        staff.emergency_contact 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {staff.emergency_contact ? 'Emergency Contact' : 'Regular'}
                      </div>
                      {staff.active !== undefined && (
                        <div className={`px-2 py-1 rounded text-xs font-medium ${
                          staff.active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {staff.active ? 'Active' : 'Inactive'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">📝 Managing Staff Contacts</h4>
            <p className="text-blue-700 text-sm mb-2">
              Staff contacts are loaded from <code>staffemails.json</code> file. Emergency contacts will receive all fire drill alerts and critical notifications.
            </p>
            <div className="text-blue-600 text-xs space-y-1">
              <div>• <strong>Emergency Contacts:</strong> Receive all emergency alerts</div>
              <div>• <strong>Regular Staff:</strong> Receive general notifications</div>
              <div>• <strong>Active/Inactive:</strong> Status determines if they receive alerts</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmergencyAlertsSimple;
